import { createRepository } from './createRepository';
import {
  EMPLOYEES_QUERY, EMPLOYEE_QUERY, CREATE_EMPLOYEE_MUTATION, UPDATE_EMPLOYEE_MUTATION,
  PAYROLL_RUNS_QUERY, PAYROLL_RUN_QUERY, RUN_PAYROLL_MUTATION, APPROVE_PAYROLL_MUTATION,
  PAY_PERIODS_QUERY,
  TIMESHEETS_QUERY, CLOCK_IN_MUTATION, CLOCK_OUT_MUTATION, WHOS_IN_QUERY, EXCEPTIONS_QUERY,
  SCHEDULE_WEEK_QUERY, CREATE_SHIFT_MUTATION,
  SAVED_REPORTS_QUERY, GENERATE_REPORT_MUTATION, AUDIT_LOG_QUERY,
  AMENDMENTS_QUERY, CREATE_AMENDMENT_MUTATION, UPDATE_AMENDMENT_MUTATION, DELETE_AMENDMENT_MUTATION,
  TIMESHEET_VERIFICATIONS_QUERY, SUBMIT_TIMESHEET_MUTATION, APPROVE_TIMESHEET_MUTATION, REJECT_TIMESHEET_MUTATION,
  BANK_ACCOUNTS_QUERY, CREATE_BANK_ACCOUNT_MUTATION, UPDATE_BANK_ACCOUNT_MUTATION, DELETE_BANK_ACCOUNT_MUTATION,
  ACCRUAL_POLICIES_QUERY, CREATE_ACCRUAL_POLICY_MUTATION, UPDATE_ACCRUAL_POLICY_MUTATION, DELETE_ACCRUAL_POLICY_MUTATION,
  POLICIES_QUERY, POLICY_DETAIL_QUERY, CREATE_POLICY_MUTATION, UPDATE_POLICY_MUTATION, DELETE_POLICY_MUTATION,
  IMPORT_JOBS_QUERY, START_IMPORT_MUTATION, EXPORT_DATA_MUTATION,
  RECURRING_SCHEDULES_QUERY, CREATE_RECURRING_SCHEDULE_MUTATION, UPDATE_RECURRING_SCHEDULE_MUTATION, DELETE_RECURRING_SCHEDULE_MUTATION,
  DOCUMENTS_QUERY, DOCUMENT_QUERY, UPLOAD_DOCUMENT_MUTATION,
  GEO_FENCES_QUERY, GEO_FENCE_QUERY, CREATE_GEO_FENCE_MUTATION, GEO_FENCE_VIOLATIONS_QUERY,
  INVOICES_QUERY, INVOICE_QUERY, CREATE_INVOICE_MUTATION,
  EXPENSE_REPORTS_QUERY, EXPENSE_REPORT_QUERY, CREATE_EXPENSE_REPORT_MUTATION,
  JOB_POSTINGS_QUERY, JOB_POSTING_QUERY, CREATE_JOB_POSTING_MUTATION,
  LEAVE_BALANCES_QUERY,
  KPIS_QUERY, REVIEWS_QUERY,
  QUALIFICATIONS_QUERY,
  REQUESTS_QUERY,
  ENHANCED_PUNCHES_QUERY, GPS_PUNCH_IN_MUTATION,
  ONBOARDING_CHECKLISTS_QUERY,
  JOBS_QUERY, JOB_DETAIL_QUERY, CREATE_JOB_MUTATION, UPDATE_JOB_MUTATION, DELETE_JOB_MUTATION,
} from '../graphql/operations';

// ---------------------------------------------------------------------------
// Employee Repository
// ---------------------------------------------------------------------------
export const useEmployeeRepository = createRepository({
  entityName: 'Employee',
  listQuery: EMPLOYEES_QUERY,
  getQuery: EMPLOYEE_QUERY,
  createMutation: CREATE_EMPLOYEE_MUTATION,
  updateMutation: UPDATE_EMPLOYEE_MUTATION,
  extractList: (data: any) => data?.employees?.data || [],
  extractEntity: (data: any) => data?.employee,
});

// ---------------------------------------------------------------------------
// Payroll Repository
// ---------------------------------------------------------------------------
export const usePayrollRepository = createRepository({
  entityName: 'PayrollRun',
  listQuery: PAYROLL_RUNS_QUERY,
  getQuery: PAYROLL_RUN_QUERY,
  createMutation: RUN_PAYROLL_MUTATION,
  extractList: (data: any) => data?.payrollRuns?.data || [],
  extractEntity: (data: any) => data?.payrollRun,
});

// ---------------------------------------------------------------------------
// Pay Period Repository
// ---------------------------------------------------------------------------
export const usePayPeriodRepository = createRepository({
  entityName: 'PayPeriod',
  listQuery: PAY_PERIODS_QUERY,
  getQuery: PAY_PERIODS_QUERY,
  extractList: (data: any) => data?.payPeriods?.data || [],
});

// ---------------------------------------------------------------------------
// Time & Attendance Repository (Payroll subgraph — Session 3)
// ---------------------------------------------------------------------------
export const useTimeAttendanceRepository = createRepository({
  entityName: 'PayrollTimesheet',
  listQuery: TIMESHEETS_QUERY,
  getQuery: TIMESHEETS_QUERY,
  createMutation: CLOCK_IN_MUTATION,
  extractList: (data: any) => data?.payrollTimesheets || [],
});

// ---------------------------------------------------------------------------
// Schedule Repository
// ---------------------------------------------------------------------------
export const useScheduleRepository = createRepository({
  entityName: 'Schedule',
  listQuery: SCHEDULE_WEEK_QUERY,
  getQuery: SCHEDULE_WEEK_QUERY,
  createMutation: CREATE_SHIFT_MUTATION,
  extractList: (data: any) => data?.scheduleWeek?.shifts || [],
});

// ---------------------------------------------------------------------------
// Report Repository
// ---------------------------------------------------------------------------
export const useReportRepository = createRepository({
  entityName: 'Report',
  listQuery: SAVED_REPORTS_QUERY,
  getQuery: SAVED_REPORTS_QUERY,
  createMutation: GENERATE_REPORT_MUTATION,
  extractList: (data: any) => data?.savedReports || [],
});

// ---------------------------------------------------------------------------
// Audit Log Repository
// ---------------------------------------------------------------------------
export const useAuditLogRepository = createRepository({
  entityName: 'AuditLog',
  listQuery: AUDIT_LOG_QUERY,
  getQuery: AUDIT_LOG_QUERY,
  extractList: (data: any) => data?.auditLog?.data || [],
});

// ---------------------------------------------------------------------------
// Amendment Repository
// ---------------------------------------------------------------------------
export const useAmendmentRepository = createRepository({
  entityName: 'Amendment',
  listQuery: AMENDMENTS_QUERY,
  getQuery: AMENDMENTS_QUERY,
  createMutation: CREATE_AMENDMENT_MUTATION,
  updateMutation: UPDATE_AMENDMENT_MUTATION,
  deleteMutation: DELETE_AMENDMENT_MUTATION,
  extractList: (data: any) => data?.amendments?.data || [],
});

// ---------------------------------------------------------------------------
// Timesheet Verification Repository
// ---------------------------------------------------------------------------
export const useTimesheetVerificationRepository = createRepository({
  entityName: 'TimesheetVerification',
  listQuery: TIMESHEET_VERIFICATIONS_QUERY,
  getQuery: TIMESHEET_VERIFICATIONS_QUERY,
  extractList: (data: any) => data?.timesheetVerifications?.data || [],
});

// ---------------------------------------------------------------------------
// Bank Account Repository
// ---------------------------------------------------------------------------
export const useBankAccountRepository = createRepository({
  entityName: 'BankAccount',
  listQuery: BANK_ACCOUNTS_QUERY,
  getQuery: BANK_ACCOUNTS_QUERY,
  createMutation: CREATE_BANK_ACCOUNT_MUTATION,
  updateMutation: UPDATE_BANK_ACCOUNT_MUTATION,
  deleteMutation: DELETE_BANK_ACCOUNT_MUTATION,
  extractList: (data: any) => data?.bankAccounts?.data || [],
});

// ---------------------------------------------------------------------------
// Accrual Policy Repository
// ---------------------------------------------------------------------------
export const useAccrualPolicyRepository = createRepository({
  entityName: 'AccrualPolicy',
  listQuery: ACCRUAL_POLICIES_QUERY,
  getQuery: ACCRUAL_POLICIES_QUERY,
  createMutation: CREATE_ACCRUAL_POLICY_MUTATION,
  updateMutation: UPDATE_ACCRUAL_POLICY_MUTATION,
  deleteMutation: DELETE_ACCRUAL_POLICY_MUTATION,
  extractList: (data: any) => data?.accrualPolicies?.data || [],
});

// ---------------------------------------------------------------------------
// Policy Repository
// ---------------------------------------------------------------------------
export const usePolicyRepository = createRepository({
  entityName: 'Policy',
  listQuery: POLICIES_QUERY,
  getQuery: POLICY_DETAIL_QUERY,
  createMutation: CREATE_POLICY_MUTATION,
  updateMutation: UPDATE_POLICY_MUTATION,
  deleteMutation: DELETE_POLICY_MUTATION,
  extractList: (data: any) => data?.policies?.data || [],
  extractEntity: (data: any) => data?.policy,
});

// ---------------------------------------------------------------------------
// Import/Export Repository
// ---------------------------------------------------------------------------
export const useImportRepository = createRepository({
  entityName: 'ImportJob',
  listQuery: IMPORT_JOBS_QUERY,
  getQuery: IMPORT_JOBS_QUERY,
  createMutation: START_IMPORT_MUTATION,
  extractList: (data: any) => data?.importJobs?.data || [],
});

// ---------------------------------------------------------------------------
// Recurring Schedule Repository
// ---------------------------------------------------------------------------
export const useRecurringScheduleRepository = createRepository({
  entityName: 'RecurringSchedule',
  listQuery: RECURRING_SCHEDULES_QUERY,
  getQuery: RECURRING_SCHEDULES_QUERY,
  createMutation: CREATE_RECURRING_SCHEDULE_MUTATION,
  updateMutation: UPDATE_RECURRING_SCHEDULE_MUTATION,
  deleteMutation: DELETE_RECURRING_SCHEDULE_MUTATION,
  extractList: (data: any) => data?.recurringSchedules?.data || [],
});

// ---------------------------------------------------------------------------
// Document Repository
// ---------------------------------------------------------------------------
export const useDocumentRepository = createRepository({
  entityName: 'Document',
  listQuery: DOCUMENTS_QUERY,
  getQuery: DOCUMENT_QUERY,
  createMutation: UPLOAD_DOCUMENT_MUTATION,
  extractList: (data: any) => data?.documents?.data || [],
  extractEntity: (data: any) => data?.document,
});

// ---------------------------------------------------------------------------
// Geo Fence Repository
// ---------------------------------------------------------------------------
export const useGeoFenceRepository = createRepository({
  entityName: 'GeoFence',
  listQuery: GEO_FENCES_QUERY,
  getQuery: GEO_FENCE_QUERY,
  createMutation: CREATE_GEO_FENCE_MUTATION,
  extractList: (data: any) => data?.geoFences?.data || [],
  extractEntity: (data: any) => data?.geoFence,
});

// ---------------------------------------------------------------------------
// Invoice Repository
// ---------------------------------------------------------------------------
export const useInvoiceRepository = createRepository({
  entityName: 'Invoice',
  listQuery: INVOICES_QUERY,
  getQuery: INVOICE_QUERY,
  createMutation: CREATE_INVOICE_MUTATION,
  extractList: (data: any) => data?.invoices?.data || [],
  extractEntity: (data: any) => data?.invoice,
});

// ---------------------------------------------------------------------------
// Expense Report Repository
// ---------------------------------------------------------------------------
export const useExpenseRepository = createRepository({
  entityName: 'ExpenseReport',
  listQuery: EXPENSE_REPORTS_QUERY,
  getQuery: EXPENSE_REPORT_QUERY,
  createMutation: CREATE_EXPENSE_REPORT_MUTATION,
  extractList: (data: any) => data?.expenseReports?.data || [],
  extractEntity: (data: any) => data?.expenseReport,
});

// ---------------------------------------------------------------------------
// KPI Repository
// ---------------------------------------------------------------------------
export const useKpiRepository = createRepository({
  entityName: 'Kpi',
  listQuery: KPIS_QUERY,
  getQuery: KPIS_QUERY,
  extractList: (data: any) => data?.kpis || [],
});

// ---------------------------------------------------------------------------
// Review Repository
// ---------------------------------------------------------------------------
export const useReviewRepository = createRepository({
  entityName: 'Review',
  listQuery: REVIEWS_QUERY,
  getQuery: REVIEWS_QUERY,
  extractList: (data: any) => data?.reviews?.data || [],
});

// ---------------------------------------------------------------------------
// Request Repository
// ---------------------------------------------------------------------------
export const useRequestRepository = createRepository({
  entityName: 'Request',
  listQuery: REQUESTS_QUERY,
  getQuery: REQUESTS_QUERY,
  extractList: (data: any) => data?.requests?.data || [],
});

// ---------------------------------------------------------------------------
// Job Costing Repository
// ---------------------------------------------------------------------------
export const useJobRepository = createRepository({
  entityName: 'Job',
  listQuery: JOBS_QUERY,
  getQuery: JOB_DETAIL_QUERY,
  createMutation: CREATE_JOB_MUTATION,
  updateMutation: UPDATE_JOB_MUTATION,
  deleteMutation: DELETE_JOB_MUTATION,
  extractList: (data: any) => data?.jobs?.data || [],
  extractEntity: (data: any) => data?.job,
});

// ---------------------------------------------------------------------------
// Recruitment Repository
// ---------------------------------------------------------------------------
export const useRecruitmentRepository = createRepository({
  entityName: 'JobPosting',
  listQuery: JOB_POSTINGS_QUERY,
  getQuery: JOB_POSTING_QUERY,
  createMutation: CREATE_JOB_POSTING_MUTATION,
  extractList: (data: any) => data?.jobPostings?.data || [],
  extractEntity: (data: any) => data?.jobPosting,
});

// ---------------------------------------------------------------------------
// Leave Balance Repository
// ---------------------------------------------------------------------------
export const useLeaveBalanceRepository = createRepository({
  entityName: 'LeaveBalance',
  listQuery: LEAVE_BALANCES_QUERY,
  getQuery: LEAVE_BALANCES_QUERY,
  extractList: (data: any) => data?.leaveBalances || [],
});

// ---------------------------------------------------------------------------
// Qualification Repository
// ---------------------------------------------------------------------------
export const useQualificationRepository = createRepository({
  entityName: 'Qualification',
  listQuery: QUALIFICATIONS_QUERY,
  getQuery: QUALIFICATIONS_QUERY,
  extractList: (data: any) => data?.qualifications || [],
});

// ---------------------------------------------------------------------------
// GPS Punch Repository (Payroll subgraph — Session 3)
// ---------------------------------------------------------------------------
export const useGpsPunchRepository = createRepository({
  entityName: 'PayrollPunchRecord',
  listQuery: ENHANCED_PUNCHES_QUERY,
  getQuery: ENHANCED_PUNCHES_QUERY,
  createMutation: GPS_PUNCH_IN_MUTATION,
  extractList: (data: any) => data?.payrollPunchRecords || [],
});

// ---------------------------------------------------------------------------
// Onboarding Repository
// ---------------------------------------------------------------------------
export const useOnboardingRepository = createRepository({
  entityName: 'Onboarding',
  listQuery: ONBOARDING_CHECKLISTS_QUERY,
  getQuery: ONBOARDING_CHECKLISTS_QUERY,
  extractList: (data: any) => data?.onboardingChecklists || [],
});
